import { FilterOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  InputNumber,
  Space,
  type TableColumnType,
} from 'antd';
import { type CheckboxChangeEvent } from 'antd/es/checkbox';
import { type FilterDropdownProps } from 'antd/es/table/interface';
import React from 'react';

import { COLOR, SPACING } from '../theme/tokens';

export enum ENumberRangeFilterKey {
  MIN = 'min',
  MAX = 'max',
  MISSING = 'missing',
}

export interface INumberRangeFilter {
  min?: number;
  max?: number;
  isMissing?: boolean;
}

const KEY_SEPARATOR = ':';

const toBound = (value: number | string | null): number | undefined => {
  if (value === null || value === '') {
    return undefined;
  }

  const bound = Number(value);

  return Number.isFinite(bound) ? bound : undefined;
};

export const toNumberRangeFilterKeys = (
  filter: INumberRangeFilter | undefined,
): string[] => {
  if (!filter) {
    return [];
  }

  if (filter.isMissing) {
    return [ENumberRangeFilterKey.MISSING];
  }

  return [
    ...(filter.min === undefined
      ? []
      : [`${ENumberRangeFilterKey.MIN}${KEY_SEPARATOR}${filter.min}`]),
    ...(filter.max === undefined
      ? []
      : [`${ENumberRangeFilterKey.MAX}${KEY_SEPARATOR}${filter.max}`]),
  ];
};

export const parseNumberRangeFilterKeys = (
  keys: readonly (React.Key | boolean)[] | null | undefined,
): INumberRangeFilter => {
  const filter: INumberRangeFilter = {};

  (keys ?? []).map(String).forEach((key: string): void => {
    const [name, value = ''] = key.split(KEY_SEPARATOR);

    if (name === ENumberRangeFilterKey.MISSING) {
      filter.isMissing = true;
    } else if (name === ENumberRangeFilterKey.MIN) {
      filter.min = toBound(value);
    } else if (name === ENumberRangeFilterKey.MAX) {
      filter.max = toBound(value);
    }
  });

  return filter;
};

export function getColumnNumberRange<T>({
  missingLabel,
}: {
  missingLabel?: string;
} = {}): Pick<TableColumnType<T>, 'filterDropdown' | 'filterIcon'> {
  return {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: FilterDropdownProps): React.ReactElement => {
      const filter: INumberRangeFilter =
        parseNumberRangeFilterKeys(selectedKeys);
      const update = (next: INumberRangeFilter): void => {
        setSelectedKeys(toNumberRangeFilterKeys({ ...filter, ...next }));
      };

      return (
        <div style={{ padding: SPACING.sm }}>
          <Space orientation="vertical">
            <Space>
              <InputNumber
                placeholder="Min"
                value={filter.min ?? null}
                disabled={filter.isMissing}
                onChange={(value: number | string | null): void => {
                  update({ min: toBound(value) });
                }}
                onPressEnter={(): void => confirm()}
              />
              <InputNumber
                placeholder="Max"
                value={filter.max ?? null}
                disabled={filter.isMissing}
                onChange={(value: number | string | null): void => {
                  update({ max: toBound(value) });
                }}
                onPressEnter={(): void => confirm()}
              />
            </Space>
            {missingLabel && (
              <Checkbox
                checked={filter.isMissing ?? false}
                onChange={(event: CheckboxChangeEvent): void => {
                  update({ isMissing: event.target.checked });
                }}
              >
                {missingLabel}
              </Checkbox>
            )}
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<FilterOutlined />}
                onClick={(): void => confirm()}
              >
                Filter
              </Button>
              <Button
                size="small"
                onClick={(): void =>
                  clearFilters?.({ confirm: true, closeDropdown: true })
                }
              >
                Reset
              </Button>
            </Space>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered: boolean): React.ReactElement => (
      <FilterOutlined style={{ color: filtered ? COLOR.primary : undefined }} />
    ),
  };
}
